import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { useLocalAuth } from './LocalAuthContext'
import LoginPage from './LoginPage'
import TrialAgreementGate from './TrialAgreementGate'
import type { ReactNode } from 'react'
import { isAzureAuthEnabled } from './authMode'

function AzureProtectedRoute({ children }: { children: ReactNode }) {
  const { inProgress } = useMsal()

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="section-shell w-full max-w-md px-8 py-10 text-center">
          <div className="terminal-kicker mb-3">Identity Handshake</div>
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-300/70 border-t-transparent" />
          <p className="text-base font-medium text-stone-100">Authenticating secure desk session...</p>
          <p className="mt-2 text-sm text-stone-500">Microsoft Entra is validating your production access.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate><LoginPage /></UnauthenticatedTemplate>
    </>
  )
}

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isLocalAuth, localUser } = useLocalAuth()

  // Local email/password auth — already verified via JWT
  if (isLocalAuth) {
    if (localUser?.is_trial && (localUser?.trial_agreement_required || localUser?.nda_required)) {
      return <TrialAgreementGate />
    }
    return <>{children}</>
  }

  // Dev bypass when Azure is not configured
  if (!isAzureAuthEnabled) {
    return <>{children}</>
  }

  return <AzureProtectedRoute>{children}</AzureProtectedRoute>
}
